<template>
  <div class="room-editor">
    <AppMapPointPanelHeader :header="header" @close="$emit('close')" />
    <div class="room-editor__content">
      <b-form>
        <slot name="floor"></slot>
        <b-form-group label="Room photo" :class="{ 'with-photo': roomTemp.avatar_id }">
          <!--      <div v-if="roomTemp.avatar_id" :style="avatarSrc" class="user-avatar"></div>-->
          <app-avatar v-if="roomTemp.avatar_id" :src="avatarSrc"></app-avatar>
          <app-file-upload
            ref="uploader"
            @upload="onUpload"
            @error="onUploadError"
            :post-url="avatarUrl"
            auto-upload
            :short-view="Boolean(roomTemp.avatar_updated_at)"
            accept="image/png,image/gif,image/jpeg,image/webp"
            :size="1024 * 1024 * 2"
            :extensions="allowedExtensions"
            :short-view-message="roomPhotoTitle"
          />
        </b-form-group>
        <b-form-group label="Name*" :state="$v.roomTemp.name.required">
          <b-input type="text" placeholder="Name" v-model="$v.roomTemp.name.$model" />
          <template #invalid-feedback v-if="$v.roomTemp.name.$dirty && !$v.roomTemp.name.required">Room name required </template>
        </b-form-group>
        <b-form-group label="Phone">
          <b-input type="text" v-model="$v.roomTemp.phone.$model" />
        </b-form-group>
        <b-form-group label="Calendar URL">
          <b-input type="text" v-model="$v.roomTemp.calendar_url.$model" />
        </b-form-group>
        <b-form-group label="Data jack nums">
          <b-input v-model="$v.roomTemp.data_jack.$model" />
        </b-form-group>
        <b-form-group label="Voice jack nums">
          <b-input v-model="$v.roomTemp.voice_jack.$model" />
        </b-form-group>
        <b-form-group label="Note">
          <b-textarea placeholder="add some note..." v-model="$v.roomTemp.note.$model" />
        </b-form-group>
        <div class="btn-panel">
          <b-btn type="button" variant="default" @click="destroy" title="Delete" v-if="can(PN.ROOM_DELETE) && !isNew" class="mr-auto">
            <i class="ion ion-md-trash"></i
          ></b-btn>
          <div class="ml-auto"></div>
          <b-btn type="button" variant="primary" @click="save">Save</b-btn>
          <b-btn type="button" variant="default" @click="cancel">Cancel</b-btn>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import nanoid from 'nanoid'
import { ROOM_CREATE, ROOM_UPDATE } from '@/store/modules/room'
import { isLocalId } from '@/utils/Id'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import * as PN from '@/utils/PermissionNames'
import { required } from 'vuelidate/lib/validators'
import AppFileUpload from '@/components/shared/AppFileUpload'
import { getAvatarSrc } from '@/utils/Helpers'
import AppAvatar from '@/components/shared/AppAvatar'
import { getItemHeader } from '@/components/shared/helpers'
import AppMapPointPanelHeader from '@/components/shared/AppMapPointPanelHeader.vue'
import escape from 'lodash/escape'

export default {
  name: 'RoomEditor',
  components: {
    AppMapPointPanelHeader,
    AppAvatar,
    AppFileUpload
  },
  props: {
    id: {
      type: [Number, String],
      required: true
    },
    point: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    roomTemp: {
      name: '',
      phone: '',
      point_id: '',
      calendar_url: '',
      is_meeting: '',
      avatar_updated_at: '',
      avatar_id: ''
    },
    PN,
    allowedExtensions: 'gif,jpg,jpeg,png,webp'
  }),
  validations() {
    return {
      roomTemp: {
        name: {
          required
        },
        phone: {},
        point_id: {},
        avatar_id: {},
        calendar_url: {},
        is_meeting: {},
        data_jack: {},
        voice_jack: {},
        note: {}
      }
    }
  },
  computed: {
    ...mapGetters('room', ['getRoom']),
    ...mapGetters('rbac', ['can']),
    header() {
      return getItemHeader(this.point, 'Room')
    },
    avatarUrl() {
      const suffix = this.roomTemp.avatar_id ? `/${this.roomTemp.avatar_id}` : ''
      return `/api/v1/avatars${suffix}`
    },
    isNew() {
      return isLocalId(this.roomTemp.id)
    },
    avatarSrc() {
      return getAvatarSrc(this.roomTemp.avatar_id, this.roomTemp.avatar_updated_at, '/img/points/icon-office-normal.svg')
    },
    roomPhotoTitle() {
      return this.roomTemp.avatar_id ? 'click to change room photo' : 'click to upload room photo'
    }
  },
  methods: {
    ...mapActions('room', ['roomSave', 'roomLoad']),
    ...mapActions('point', ['pointSave']),
    ...mapMutations('room', {
      roomCreate_: ROOM_CREATE,
      roomUpdate_: ROOM_UPDATE
    }),
    save() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        const pointData = { id: this.point.id, title: this.roomTemp.name }
        const pointPromise = this.pointSave(pointData)

        pointPromise.then((pointId) => {
          this.roomTemp.point_id = pointId
          const payload = { room: this.roomTemp, config: { params: { expand: 'point' } } }
          this.roomSave(payload).then(() => {
            this.$emit('save', { pointId })
          })
        })
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    destroy() {
      this.$appToast.question('A you sure want to delete room?').then(() => {
        this.$emit('destroy')
      })
    },
    onUpload(avatar) {
      this.roomTemp.avatar_id = avatar.id
      this.roomTemp.avatar_updated_at = nanoid()
      this.$appToast.success('Room photo uploaded!')
    },
    onUploadError({ cause, response }) {
      let errorMessages = []
      if (cause === 'denied' && response.length) {
        response.forEach((error) => {
          errorMessages.push(error.message)
        })
      } else if (cause === 'size') {
        errorMessages.push('File is too big. Its size cannot exceed 2 MiB.')
      } else if (cause === 'extension') {
        errorMessages.push(`File extension is not allowed. Support ${this.allowedExtensions}.`)
      } else {
        errorMessages.push(`Unknown error on file upload. Please write to support.`)
      }
      this.$appToast.error(errorMessages.map(escape).join('<br>'))
    }
  },
  watch: {
    id: {
      immediate: true,
      handler(id) {
        this.roomTemp = Object.assign({ id }, this.roomTemp, this.getRoom(id))
      }
    }
  }
}
</script>

<style>
.room-editor__content {
  padding: 24px;
}
</style>
